import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';
import { PropertyOverlay } from '../components/PropertyOverlay';
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Pin,
  InfoWindow,
  type MapMouseEvent,
} from '@vis.gl/react-google-maps';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBNpVf2wxLdPMDj6_VAKG_KM179d7sV2vI';
const PROPERTY_CENTER = { lat: 44.866701, lng: -77.381202 };

const POI_TYPES = [
  { id: 'stand', label: 'Stand', color: '#28A745' },
  { id: 'blind', label: 'Blind', color: '#1B3B1B' },
  { id: 'camera', label: 'Trail Camera', color: '#6F42C1' },
  { id: 'feeding', label: 'Feeding', color: '#FF6B35' },
  { id: 'sighting', label: 'Sighting', color: '#E83E8C' },
  { id: 'dock', label: 'Dock', color: '#17A2B8' },
  { id: 'other', label: 'Other', color: '#888' },
];
const TYPE_MAP = new window.Map(POI_TYPES.map((t) => [t.id, t]));

interface POI {
  id: string;
  name: string;
  type: string;
  locationLat: string;
  locationLon: string;
}

type PanelMode =
  | { type: 'none' }
  | { type: 'confirm'; lat: number; lng: number }
  | { type: 'create'; lat: number; lng: number }
  | { type: 'edit'; poi: POI }
  | { type: 'view'; poi: POI };

export function POIsPage() {
  const [pois, setPois] = useState<POI[]>([]);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState<PanelMode>({ type: 'none' });

  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('stand');
  const [editLat, setEditLat] = useState('');
  const [editLon, setEditLon] = useState('');

  useEffect(() => { loadPois(); }, []);

  async function loadPois() {
    setLoading(true);
    const res = await apiFetch<POI[]>('/api/admin/map/pois');
    if (res.success && res.data) setPois(res.data);
    setLoading(false);
  }

  const handleMapClick = useCallback((e: MapMouseEvent) => {
    if (panel.type !== 'none' && panel.type !== 'view') return;
    if (!e.detail.latLng) return;
    setPanel({ type: 'confirm', lat: e.detail.latLng.lat, lng: e.detail.latLng.lng });
  }, [panel.type]);

  function confirmPin() {
    if (panel.type !== 'confirm') return;
    setFormName('');
    setFormType('stand');
    setPanel({ type: 'create', lat: panel.lat, lng: panel.lng });
  }

  function openEdit(poi: POI) {
    setFormName(poi.name);
    setFormType(poi.type);
    setEditLat(poi.locationLat);
    setEditLon(poi.locationLon);
    setPanel({ type: 'edit', poi });
  }

  async function savePoi() {
    if (panel.type === 'create') {
      await apiFetch('/api/admin/map/pois', {
        method: 'POST',
        body: JSON.stringify({ name: formName, type: formType, lat: panel.lat, lon: panel.lng }),
      });
    } else if (panel.type === 'edit') {
      await apiFetch(`/api/admin/map/pois/${panel.poi.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: formName, type: formType, lat: parseFloat(editLat), lon: parseFloat(editLon) }),
      });
    }
    setPanel({ type: 'none' });
    loadPois();
  }

  async function deletePoi(poi: POI) {
    if (!confirm(`Delete "${poi.name}"?`)) return;
    await apiFetch(`/api/admin/map/pois/${poi.id}`, { method: 'DELETE' });
    setPanel({ type: 'none' });
    loadPois();
  }

  return (
    <div style={s.page}>
      <div style={s.mapCol}>
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            defaultCenter={PROPERTY_CENTER}
            defaultZoom={15}
            mapTypeId="satellite"
            gestureHandling="greedy"
            fullscreenControl={false}
            mapId="grimsthorpe-admin-pois"
            onClick={handleMapClick}
            style={{ width: '100%', height: '100%' }}
          >
            <PropertyOverlay />
            {pois.map((poi) => {
              const isEditing = panel.type === 'edit' && panel.poi.id === poi.id;
              const lat = isEditing ? parseFloat(editLat) : parseFloat(poi.locationLat);
              const lng = isEditing ? parseFloat(editLon) : parseFloat(poi.locationLon);
              return (
                <AdvancedMarker
                  key={poi.id}
                  position={{ lat, lng }}
                  title={poi.name}
                  draggable={isEditing}
                  onClick={() => { if (!isEditing) setPanel({ type: 'view', poi }); }}
                  onDragEnd={(e) => {
                    if (isEditing && e.latLng) {
                      setEditLat(e.latLng.lat().toFixed(7));
                      setEditLon(e.latLng.lng().toFixed(7));
                    }
                  }}
                >
                  <Pin background={TYPE_MAP.get(poi.type)?.color || '#888'} borderColor={isEditing ? '#00BFFF' : '#000'} glyphColor="#FFF" scale={0.8} />
                </AdvancedMarker>
              );
            })}

            {panel.type === 'confirm' && (
              <AdvancedMarker
                position={{ lat: panel.lat, lng: panel.lng }}
                draggable
                onDragEnd={(e) => { if (e.latLng) setPanel({ type: 'confirm', lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
              >
                <Pin background="#FFC107" borderColor="#FF8F00" glyphColor="#000" />
              </AdvancedMarker>
            )}

            {panel.type === 'view' && (
              <InfoWindow
                position={{ lat: parseFloat(panel.poi.locationLat), lng: parseFloat(panel.poi.locationLon) }}
                onCloseClick={() => setPanel({ type: 'none' })}
                pixelOffset={[0, -40]}
              >
                <div style={{ color: '#000', minWidth: 150 }}>
                  <strong>{panel.poi.name}</strong>
                  <p style={{ margin: '4px 0', fontSize: 11 }}>{TYPE_MAP.get(panel.poi.type)?.label || panel.poi.type}</p>
                  <button onClick={() => openEdit(panel.poi)}
                    style={{ marginTop: 4, padding: '4px 12px', fontSize: 12, backgroundColor: '#1B3B1B', color: '#FFD700', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                    Edit POI
                  </button>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </APIProvider>
      </div>

      <div style={s.panelCol}>
        <div style={s.panelContent}>
          {panel.type === 'none' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={s.panelTitle}>Points of Interest</h3>
              </div>
              <p style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 16 }}>Click the map to place a new POI.</p>
              {loading ? <div style={{ color: COLORS.textMuted }}>Loading...</div> :
                pois.length === 0 ? <p style={{ color: COLORS.textMuted, fontSize: 13, fontStyle: 'italic' }}>No POIs yet.</p> :
                pois.map((poi) => (
                  <div key={poi.id} style={s.itemRow} onClick={() => setPanel({ type: 'view', poi })}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: TYPE_MAP.get(poi.type)?.color || '#888', flexShrink: 0 }} />
                    <div>
                      <div style={{ color: COLORS.textPrimary, fontSize: 13, fontWeight: 500 }}>{poi.name}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: 11 }}>{TYPE_MAP.get(poi.type)?.label || poi.type}</div>
                    </div>
                  </div>
                ))
              }
            </>
          )}

          {panel.type === 'confirm' && (
            <>
              <h3 style={s.panelTitle}>Confirm Location</h3>
              <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 12 }}>Drag the yellow pin to adjust.</p>
              <p style={{ color: COLORS.textSecondary, fontSize: 12, fontFamily: 'monospace' }}>{panel.lat.toFixed(6)}, {panel.lng.toFixed(6)}</p>
              <div style={s.panelActions}>
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Cancel</button>
                <button style={s.btnPrimary} onClick={confirmPin}>Confirm & Create</button>
              </div>
            </>
          )}

          {(panel.type === 'create' || panel.type === 'edit') && (
            <>
              <h3 style={s.panelTitle}>{panel.type === 'edit' ? 'Edit POI' : 'New POI'}</h3>
              <div style={s.field}>
                <label style={s.label}>Name</label>
                <input value={formName} onChange={(e) => setFormName(e.target.value)} style={s.input} autoFocus />
              </div>
              <div style={s.field}>
                <label style={s.label}>Type</label>
                <select value={formType} onChange={(e) => setFormType(e.target.value)} style={s.input}>
                  {POI_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {POI_TYPES.map((t) => (
                  <button key={t.id} onClick={() => setFormType(t.id)}
                    style={{ padding: '4px 10px', borderRadius: 12, border: formType === t.id ? `2px solid ${t.color}` : `1px solid ${COLORS.borderDefault}`,
                      backgroundColor: formType === t.id ? `${t.color}22` : 'transparent', color: formType === t.id ? t.color : COLORS.textMuted, fontSize: 11, cursor: 'pointer' }}>
                    {t.label}
                  </button>
                ))}
              </div>
              <div style={s.panelActions}>
                {panel.type === 'edit' && <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={() => deletePoi(panel.poi)}>Delete</button>}
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Cancel</button>
                <button style={s.btnPrimary} onClick={savePoi} disabled={!formName.trim()}>
                  {panel.type === 'edit' ? 'Update' : 'Create'}
                </button>
              </div>
            </>
          )}

          {panel.type === 'view' && (
            <>
              <h3 style={s.panelTitle}>{panel.poi.name}</h3>
              <span style={{ ...s.badge, backgroundColor: `${TYPE_MAP.get(panel.poi.type)?.color || '#888'}22`, color: TYPE_MAP.get(panel.poi.type)?.color || '#888' }}>
                {TYPE_MAP.get(panel.poi.type)?.label || panel.poi.type}
              </span>
              <p style={{ color: COLORS.textSecondary, fontSize: 12, marginTop: 12, fontFamily: 'monospace' }}>
                {panel.poi.locationLat}, {panel.poi.locationLon}
              </p>
              <div style={s.panelActions}>
                <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={() => deletePoi(panel.poi)}>Delete</button>
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Close</button>
                <button style={s.btnPrimary} onClick={() => openEdit(panel.poi)}>Edit</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { display: 'flex', height: 'calc(100vh - 60px)', gap: 0 },
  mapCol: { flex: 1, minWidth: 0 },
  panelCol: { width: 360, flexShrink: 0, overflowY: 'auto', borderLeft: `1px solid ${COLORS.borderDefault}`, backgroundColor: COLORS.backgroundSecondary },
  panelContent: { padding: 20 },
  panelTitle: { fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: '0 0 16px' },
  panelActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  itemRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', marginBottom: 4, borderRadius: 6, border: `1px solid ${COLORS.borderDefault}`, cursor: 'pointer' },
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { padding: '7px 10px', backgroundColor: COLORS.backgroundTertiary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, color: COLORS.textPrimary, fontSize: 13 },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  btnPrimary: { padding: '7px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '7px 14px', backgroundColor: COLORS.backgroundTertiary, color: COLORS.textSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnDanger: { padding: '7px 14px', backgroundColor: 'transparent', color: COLORS.statusError, border: `1px solid rgba(220,53,69,0.3)`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
};
